import React, { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Platform, PressableStateCallbackType, ScrollView, View, ViewStyle } from 'react-native'

import { BottomSheetFlashList } from '@gorhom/bottom-sheet'
import { FlashList, FlashListRef, ListRenderItem } from '@shopify/flash-list'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { includes } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { sectionListHeaderMessages } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/typeAheadUtils'

import { Item, TypeAheadCustomOptionsConfig } from './TypeAheadCustomOptionsConfig'
import { AccessibilityRolesNative, IS_WEB } from '../../../constants'
import { MenuItemProps } from '../../../organisms/menu/Menu'
import { DisplaySize } from '../../../styles/typeStyles'
import { ThemeType } from '../../../utils/themes/ThemeProvider'
import { tID } from '../../../utils/utils'
import { BodyText, Size as BodyTextSize } from '../../bodyText/BodyText'
import { Display } from '../../display/Display'
import { Divider } from '../../divider/Divider'
import { CheckMarkIcon } from '../../icons/CheckMarkIcon'

export enum MENU_SELECTED_ITEM_STYLE {
  CHECKMARK = 'checkmark',
  LEFT_BORDER = 'leftBorder',
}

const ITEM_HEIGHT = 40
const MAX_VISIBLE_ITEMS = 6

export type GetMenuItemStyle = (args: {
  hovered?: boolean
  pressed?: boolean
  focused?: boolean
  selected?: boolean
  text?: string
}) => ViewStyle

export interface DropdownMenuItemProps<T> {
  onPress: (text: string, customSelectedItem?: T) => void
  option: string
  highlightValue?: string
  selected?: boolean
  customOptionsConfig?: TypeAheadCustomOptionsConfig<T>
  customOption?: T
  value: string[]
  item?: Item
  selectedItemStyle?: MENU_SELECTED_ITEM_STYLE | null
  getMenuItemStyle?: GetMenuItemStyle
  disabled?: boolean
  idPrefix?: string
  index: number
  /** Use simple header style (smaller text, secondary color) */
  simpleHeaderStyle?: boolean
  /** Use active/teal color for menu item text */
  useActiveTextColor?: boolean
  itemRole?: DropdownMenuItemRole
}

export type DropdownMenuItemRole = 'option' | 'menuitem'

export interface DropdownMenuProps<T> {
  onOptionPress: (text: string, customSelectedItem?: T) => void
  options: string[]
  highlightValue?: string
  optionSelected?: number
  customOptionsConfig?: TypeAheadCustomOptionsConfig<T>
  /** Subset of the `options` list in `customOptionsConfig` (filtered by search query) */
  customOptions?: T[]
  /** List of all selected options */
  value: string[]
  initialScrollIndex?: number
  selectedItemStyle?: MENU_SELECTED_ITEM_STYLE | null
  showsVerticalScrollIndicator?: boolean
  scrollEnabled?: boolean
  getMenuItemStyle?: GetMenuItemStyle
  listFooterComponent?: React.ReactElement
  listFooterComponentStyle?: ViewStyle
  scrollEnabledWithinBottomSheet?: boolean
  idPrefix?: string
  /** Use simple header style (smaller text, secondary color) */
  simpleHeaderStyle?: boolean
  /** Use active/teal color for menu item text */
  useActiveTextColor?: boolean
  /** Limit the dropdown to the same six visible rows used by typeahead dropdowns. */
  constrainHeight?: boolean
  itemRole?: DropdownMenuItemRole
}

const MenuItem = styled.Pressable({
  flexDirection: 'row',
  borderRadius: '4px',
})

const DisplayTextContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const CheckMarkIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingLeft: Platform.OS === 'web' ? theme.spacing['16px'] : '36px',
}))

const SelectedItemLeftBorder = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  borderTopRightRadius: theme.spacing['16px'],
  borderBottomRightRadius: theme.spacing['16px'],
  backgroundColor: theme.colors.backgroundActive,
  width: '4px',
  top: '3px',
  bottom: '3px',
  left: 0,
}))

const SectionHeader = styled(BodyText)({
  flexDirection: 'row',
  padding: '12px 16px 8px 16px',
})

const SectionHeaderEyebrow = styled(Display)(({ theme }) => ({
  padding: `${theme.spacing['8px']} ${theme.spacing['16px']}`,
  color: theme.colors.textSecondary,
}))

const SimpleHeader = styled(BodyText)<{ theme: ThemeType; isFirst?: boolean }>(({ theme, isFirst }) => ({
  padding: '12px 16px 8px 16px',
  color: theme.colors.textSecondary,
  ...(isFirst ? {} : { borderTopWidth: 1, borderTopColor: theme.colors.borderDefault }),
}))

const DropdownMenuItemNotMemoized = <T,>({
  onPress,
  option,
  highlightValue = '',
  selected,
  customOptionsConfig,
  customOption,
  value,
  item,
  selectedItemStyle,
  disabled = false,
  getMenuItemStyle,
  idPrefix,
  index,
  simpleHeaderStyle = false,
  useActiveTextColor = false,
  itemRole = 'option',
}: DropdownMenuItemProps<T>): JSX.Element => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const menuItemPadding = { paddingVertical: 8, paddingHorizontal: 16 }
  const { menuItem_header, menuItem_divider, menuItem_section, useEyebrow } = item || {}
  if (menuItem_header) {
    // Check if there's a predefined i18n message for this header, otherwise use the raw text
    const headerMessage = sectionListHeaderMessages[option as keyof typeof sectionListHeaderMessages]
    const headerText = headerMessage ? formatMessage(headerMessage) : option
    if (simpleHeaderStyle) {
      return (
        <SimpleHeader
          text={headerText}
          size={BodyTextSize.SMALL}
          isFirst={index === 0}
          testID={tID(`typeAhead-menu-section-header-${option}`)}
        />
      )
    }
    if (useEyebrow) {
      return <SectionHeaderEyebrow size={DisplaySize.EYEBROW} text={headerText} />
    }
    return (
      <SectionHeader
        text={headerText}
        size={BodyTextSize.LARGE}
        testID={tID(`typeAhead-menu-section-header-${option}`)}
      />
    )
  } else if (menuItem_divider) {
    return <Divider height={1} style={{ marginTop: 12, marginBottom: 12 }} />
  }

  const stringMatches = match(option, highlightValue, { insideWords: true })
  const parsedText = parse(option, stringMatches)
  const valueToMatch = customOption ? customOptionsConfig?.getValueForItem(customOption) : option
  const isSelectedOption = value?.length ? includes(value, valueToMatch) : value && value[0] === valueToMatch
  const accessibilityLabel = formatMessage(
    {
      defaultMessage: 'List item {option}, {isSectionItem, select, true {section {sectionName}} other {}}',
      description: 'Accessibility label for an item in a dropdown list',
    },
    {
      option,
      sectionName: menuItem_section,
      isSectionItem: !!menuItem_section,
    },
  )
  const defaultTextColor = useActiveTextColor ? colors.textActive : colors.textPrimary
  const getHighlightedText = (extraProps?: any) => {
    return parsedText.map(({ text, highlight }) => (
      <BodyText
        key={`${text}-${highlight}`}
        text={text}
        size={BodyTextSize.DEFAULT}
        underline={highlight}
        bold={highlight}
        color={defaultTextColor}
        {...extraProps}
        accessibilityLabel={accessibilityLabel}
      />
    ))
  }
  const displayText = isSelectedOption ? (
    <DisplayTextContainer>
      {getHighlightedText({ color: colors.textActive })}
      {selectedItemStyle === MENU_SELECTED_ITEM_STYLE.CHECKMARK && (
        <CheckMarkIconContainer>
          <CheckMarkIcon />
        </CheckMarkIconContainer>
      )}
    </DisplayTextContainer>
  ) : (
    <DisplayTextContainer>{getHighlightedText()}</DisplayTextContainer>
  )

  const menuItemContent =
    customOption && customOptionsConfig?.menuItemRenderer
      ? customOptionsConfig?.menuItemRenderer(customOption, displayText, isSelectedOption)
      : displayText

  return (
    <MenuItem
      accessible
      focusable
      disabled={disabled}
      onPress={() => onPress(option, customOption)}
      onKeyDown={(event) => {
        if (event?.key === 'Enter') {
          onPress(option, customOption)
        }
      }}
      testID={tID((customOption && customOptionsConfig?.getOptionTestId(customOption)) || 'typeAhead-menu-item')}
      style={({ hovered, pressed, focused }: PressableStateCallbackType) => {
        const baseStyles = [menuItemPadding]
        if (disabled) {
          return [{ opacity: 0.5 }, ...baseStyles]
        }
        return [
          (hovered || selected || isSelectedOption) && { backgroundColor: colors.typeaheadItemBackgroundHover },
          pressed && { backgroundColor: colors.tertiaryButtonBackgroundPressed },
          focused && {
            outlineStyle: 'solid',
            outlineColor: colors.borderFocus,
            outlineWidth: 2,
            outlineOffset: -2,
          },
          ...baseStyles,
          getMenuItemStyle && getMenuItemStyle({ hovered, pressed, focused, selected: isSelectedOption, text: option }),
        ]
      }}
      accessibilityLabel={accessibilityLabel}
      {...(IS_WEB
        ? {
            role: itemRole,
            'aria-label': option,
            ...(itemRole === 'option' && { 'aria-selected': isSelectedOption }),
            ...(itemRole === 'menuitem' && isSelectedOption && { 'aria-current': true }),
            ...(itemRole === 'menuitem' && disabled && { 'aria-disabled': true }),
            tabIndex: 0,
            ...(idPrefix ? { id: `${idPrefix}-option-${index}` } : {}),
          }
        : {
            accessibilityRole: AccessibilityRolesNative.BUTTON,
            accessibilityState: { disabled, selected: isSelectedOption },
          })}
    >
      {isSelectedOption && selectedItemStyle === MENU_SELECTED_ITEM_STYLE.LEFT_BORDER && <SelectedItemLeftBorder />}
      {menuItemContent}
    </MenuItem>
  )
}
export const DropdownMenuItem = React.memo(DropdownMenuItemNotMemoized)

/**
 * A dropdown component that is used to display a list of items
 * - Currently used in `TypeAhead`, `DropdownButton`, and `Menu`
 *
 * todo: move component so that it is separate from the typeahead
 */
export const DropdownMenu = <T,>({
  options,
  onOptionPress,
  highlightValue,
  optionSelected,
  customOptionsConfig,
  customOptions,
  value,
  initialScrollIndex,
  selectedItemStyle = MENU_SELECTED_ITEM_STYLE.CHECKMARK,
  getMenuItemStyle,
  showsVerticalScrollIndicator,
  scrollEnabled,
  listFooterComponent,
  listFooterComponentStyle,
  // Use mobile-specific container to render items which fixes bug where container not scrollable
  scrollEnabledWithinBottomSheet = false,
  idPrefix,
  simpleHeaderStyle = false,
  useActiveTextColor = false,
  constrainHeight = true,
  itemRole = 'option',
}: DropdownMenuProps<T>): JSX.Element => {
  const ref = useRef<FlashListRef<string> | null>(null)
  const webRef = useRef<ScrollView | null>(null)
  // Follow highlighted items and reset when filtering or the list becomes ready, but not on every option-count change.
  const hasOptions = options.length > 0
  useEffect(() => {
    if (hasOptions) {
      const index = optionSelected !== undefined && optionSelected >= 0 ? optionSelected : (initialScrollIndex ?? 0)
      if (IS_WEB && webRef.current) {
        webRef.current.scrollTo({ animated: true, y: index * ITEM_HEIGHT })
      } else if (ref.current) {
        ref.current.scrollToIndex({ animated: true, index })
      }
    }
  }, [hasOptions, highlightValue, initialScrollIndex, optionSelected])

  const renderDefaultItem = (item: string, index: number) => {
    return (
      <DropdownMenuItem
        option={item}
        onPress={onOptionPress}
        highlightValue={highlightValue}
        selected={optionSelected === index}
        value={value}
        selectedItemStyle={selectedItemStyle}
        getMenuItemStyle={getMenuItemStyle}
        idPrefix={idPrefix}
        index={index}
        simpleHeaderStyle={simpleHeaderStyle}
        useActiveTextColor={useActiveTextColor}
        itemRole={itemRole}
      />
    )
  }
  const renderItem: ListRenderItem<string> = ({ item, index }) => renderDefaultItem(item, index)

  const renderCustomItem = (item: T, index: number) => {
    return (
      <DropdownMenuItem
        option={customOptionsConfig?.getDisplayString(item) || ''}
        onPress={onOptionPress}
        highlightValue={highlightValue}
        selected={optionSelected === index}
        customOptionsConfig={customOptionsConfig}
        customOption={item}
        value={value}
        item={item as Item}
        selectedItemStyle={selectedItemStyle}
        getMenuItemStyle={getMenuItemStyle}
        disabled={(item as MenuItemProps).disabled}
        idPrefix={idPrefix}
        index={index}
        simpleHeaderStyle={simpleHeaderStyle}
        useActiveTextColor={useActiveTextColor}
        itemRole={itemRole}
      />
    )
  }
  const renderItemCustom: ListRenderItem<T> = ({ item, index }) => renderCustomItem(item, index)
  const keyExtractor = (option: string) => option
  const keyExtractorCustom = (option: T) => customOptionsConfig?.getStableIdForItem(option) ?? ''
  // Native needs the adapter so pan gestures are coordinated with the bottom sheet.
  const BottomSheetContainer = scrollEnabledWithinBottomSheet ? BottomSheetFlashList : FlashList

  if (IS_WEB) {
    const webOptions = customOptionsConfig && customOptions ? customOptions : options

    return (
      <ScrollView
        ref={webRef}
        style={constrainHeight ? { maxHeight: ITEM_HEIGHT * MAX_VISIBLE_ITEMS } : undefined}
        showsVerticalScrollIndicator={showsVerticalScrollIndicator}
        scrollEnabled={scrollEnabled}
      >
        {webOptions.map((item, index) => {
          const key = customOptionsConfig ? keyExtractorCustom(item as T) : keyExtractor(item as string)
          return (
            <React.Fragment key={key}>
              {customOptionsConfig ? renderCustomItem(item as T, index) : renderDefaultItem(item as string, index)}
            </React.Fragment>
          )
        })}
        {listFooterComponent && <View style={listFooterComponentStyle}>{listFooterComponent}</View>}
      </ScrollView>
    )
  }

  return (
    <>
      {/* Default string option items */}
      {!customOptionsConfig && (
        <BottomSheetContainer
          data={options}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          initialScrollIndex={initialScrollIndex}
          /* FlashList will not re-render the list if a prop in the renderItem function changes
             so to ensure the highlighting of text disappears after a user backspaces all characters, include that in extraData.
             Issue here: https://github.com/Shopify/flash-list/issues/699 */
          extraData={{ highlightValue, value }}
          ref={(instance: React.FC | FlashListRef<string> | null) => {
            // BottomSheetFlashList types its ref as a component, but forwards the FlashList imperative handle at runtime.
            ref.current = instance as unknown as FlashListRef<string> | null
          }}
          showsVerticalScrollIndicator={showsVerticalScrollIndicator}
          scrollEnabled={scrollEnabled}
          {...(!IS_WEB && { maintainVisibleContentPosition: { disabled: true } })}
          ListFooterComponent={listFooterComponent}
          ListFooterComponentStyle={listFooterComponentStyle}
        />
      )}

      {/* Custom option items, rendered using `TypeAheadCustomOptionsConfig menuItemRenderer()` */}
      {customOptionsConfig && customOptions && (
        <FlashList
          data={customOptions}
          renderItem={renderItemCustom}
          keyExtractor={keyExtractorCustom}
          {...(!IS_WEB && { drawDistance: ITEM_HEIGHT })} // render ahead offset in px
          initialScrollIndex={initialScrollIndex}
          extraData={{ highlightValue, value }}
          showsVerticalScrollIndicator={showsVerticalScrollIndicator}
          scrollEnabled={scrollEnabled}
          {...(!IS_WEB && { maintainVisibleContentPosition: { disabled: true } })}
          ListFooterComponent={listFooterComponent}
          ListFooterComponentStyle={listFooterComponentStyle}
        />
      )}
    </>
  )
}
